<template>
  <div class="organization-settings mt-5">

    <!-- Dashboard config -->
    <v-card>
      <v-card-title>
        <h3>{{ $t('dashboard-config.title') }}</h3>
        <v-spacer />
        <v-btn v-if="currentUser.isZE" small color="primary" :to="{name: 'organizations.dashboard', query: {ze: '1'}}">
          <v-icon left small>mdi-monitor-dashboard</v-icon>
          Dashboard ZE
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-switch
                v-model="form.enabled"
                :label="$t('dashboard-config.enabled')"
                hide-details
            />
          </v-col>
        </v-row>

        <div v-if="form.enabled" class="mt-4">
          <v-divider class="mb-4" />

          <!-- Filter mode toggle -->
          <h4 class="mb-3">{{ $t('dashboard-config.filter-mode') }}</h4>
          <v-btn-toggle v-model="form.filterMode" mandatory dense class="filter-mode-toggle mb-4">
            <v-btn v-for="fm in filterModeOptions" :key="fm.value" :value="fm.value" small>
              {{ fm.text }}
            </v-btn>
          </v-btn-toggle>

          <v-divider class="mb-4" />

          <!-- Options -->
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                  v-model="form.hideAvailability"
                  :label="$t('dashboard-config.hide-availability')"
                  hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-checkbox
                  v-model="form.enableReport"
                  :label="$t('dashboard-config.enable-report')"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Tabs -->
          <h4 class="mb-3">{{ $t('dashboard-config.tabs') }}</h4>
          <v-row>
            <v-col cols="12" md="4">
              <v-checkbox
                  v-model="form.tabs.fleetActivity"
                  :label="$t('dashboard-config.fleet-activity')"
                  hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox
                  v-model="form.tabs.drivers"
                  :label="$t('dashboard-config.drivers')"
                  hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox
                  v-model="form.tabs.operations"
                  :label="$t('dashboard-config.operations')"
                  hide-details
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Axes de découpage des bilans : régions, filiales… propres à chaque client -->
          <h4 class="mb-1">{{ $t('dashboard-config.report-axes.title') }}</h4>
          <p class="text-caption grey--text mb-3">{{ $t('dashboard-config.report-axes.help') }}</p>
          <v-row v-for="(axis, index) in form.reportAxes" :key="index" dense align="center">
            <v-col cols="12" md="4">
              <v-text-field
                  v-model="axis.label"
                  :label="$t('dashboard-config.report-axes.label')"
                  :placeholder="$t('dashboard-config.report-axes.label-placeholder')"
                  maxlength="60"
                  outlined
                  dense
                  hide-details
              />
            </v-col>
            <v-col cols="12" md="7">
              <v-autocomplete
                  v-model="axis.organizationId"
                  :items="axisCandidates"
                  item-value="id"
                  item-text="path"
                  :loading="loadingAxisCandidates"
                  :label="$t('dashboard-config.report-axes.organization')"
                  :hint="axisHint(axis.organizationId)"
                  persistent-hint
                  outlined
                  dense
              >
                <!-- On choisit le parent : ses enfants directs deviennent les lignes du tableau. -->
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.path }}</v-list-item-title>
                    <v-list-item-subtitle>{{ rowsPreview(item) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="1" class="text-right">
              <v-btn icon small @click="form.reportAxes.splice(index, 1)"><v-icon small>mdi-delete</v-icon></v-btn>
            </v-col>
          </v-row>
          <v-btn small text color="primary" class="mt-2" @click="addAxis">
            <v-icon left small>mdi-plus</v-icon>{{ $t('dashboard-config.report-axes.add') }}
          </v-btn>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn small color="primary" @click="save" :loading="saving">
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

  </div>
</template>

<script>
import OrganizationRepository from "@repository/OrganizationRepository";

export default {

  data() {
    return {
      saving: false,
      form: {
        enabled: false,
        tabs: {
          fleetActivity: true,
          drivers: true,
          operations: false,
        },
        filterMode: 'hybrid',
        hideAvailability: false,
        enableReport: false,
        reportAxes: [],
      },
      axisCandidates: [],
      loadingAxisCandidates: false,
    }
  },

  mounted() {
    this.loadConfig()
    this.loadAxisCandidates()
  },

  computed: {
    organization() {
      return this.$parent.organization ?? null
    },

    filterModeOptions() {
      return [
        { text: this.$t('dashboard-config.filter-modes.all'), value: 'all' },
        { text: this.$t('dashboard-config.filter-modes.active'), value: 'active' },
        { text: this.$t('dashboard-config.filter-modes.hybrid'), value: 'hybrid' },
        { text: this.$t('dashboard-config.filter-modes.sim-connected'), value: 'sim-connected' },
        { text: this.$t('dashboard-config.filter-modes.home'), value: 'home' },
        { text: this.$t('dashboard-config.filter-modes.away-from-home'), value: 'away-from-home' },
      ]
    },
  },

  methods: {
    loadConfig() {
      if (!this.organization) return
      const config = this.organization.dashboardConfig
      if (config) {
        this.form = {
          enabled: config.enabled ?? false,
          tabs: {
            fleetActivity: config.tabs?.fleetActivity ?? true,
            drivers: config.tabs?.drivers ?? true,
            operations: config.tabs?.operations ?? false,
          },
          filterMode: config.filterMode ?? 'all',
          hideAvailability: config.hideAvailability ?? false,
          enableReport: config.enableReport ?? false,
          reportAxes: (config.reportAxes ?? []).map(axis => ({ ...axis })),
        }
      }
    },

    /** Les organisations de l'arborescence qui ont des enfants : chacune peut servir d'axe. */
    loadAxisCandidates() {
      if (!this.organization) return
      this.loadingAxisCandidates = true
      OrganizationRepository.getReportAxisCandidates(this.organization.id).then(response => {
        this.axisCandidates = response.organizations || []
      }).catch(e => {
        this.notifyError(e)
      }).finally(() => {
        this.loadingAxisCandidates = false
      })
    },

    addAxis() {
      this.form.reportAxes.push({ label: '', organizationId: null })
    },

    /** « 4 lignes : Proxiserve - NE, Proxiserve - IDF Normandie… » */
    axisHint(organizationId) {
      const candidate = this.axisCandidates.find(c => c.id === organizationId)
      return candidate ? this.rowsPreview(candidate) : ''
    },

    /** « 6 lignes : PXS Maintenance - IdF Normandie, PXS Maintenance - NE… » */
    rowsPreview(candidate) {
      const rows = candidate.rows || []
      const shown = rows.slice(0, 4).join(', ') + (rows.length > 4 ? '…' : '')
      return this.$t('dashboard-config.report-axes.rows', { count: candidate.children, rows: shown })
    },

    save() {
      this.saving = true
      OrganizationRepository.saveDashboardConfig(this.organization.id, this.form).then((response) => {
        this.saving = false
        this.$dialog.notify.success(this.$t('dashboard-config.saved'))
        if (response.dashboardConfig) {
          this.organization.dashboardConfig = response.dashboardConfig
        }
      }).catch(e => {
        this.saving = false
        this.notifyError(e)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-mode-toggle {
  &.v-btn-toggle {
    border-radius: 6px;
    .v-btn {
      height: 32px !important;
      font-size: 11px !important;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
      padding: 0 10px !important;
      min-width: auto !important;
    }
    .v-btn--active {
      background-color: #FF5722 !important;
      color: white !important;
    }
  }
}
</style>
